/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { PropsWithChildren, SyntheticEvent } from 'react';
import React, { PureComponent, Fragment, useEffect, useState, useMemo } from 'react';

/* eslint-disable @typescript-eslint/consistent-type-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766; jsx required at runtime for @jsxRuntime classic */
import { jsx, css } from '@emotion/react';

import type { AnnotationMarkDefinition } from '@atlaskit/adf-schema/annotation';
import { AnnotationMarkStates } from '@atlaskit/adf-schema/annotation';
import type { BorderMarkDefinition } from '@atlaskit/adf-schema/border';
import type { DataConsumerDefinition } from '@atlaskit/adf-schema/data-consumer';
import type { LinkDefinition } from '@atlaskit/adf-schema/link';
import { MEDIA_CONTEXT } from '@atlaskit/analytics-namespaced-context/MediaAnalyticsContext';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import { VIEW_METHOD } from '@atlaskit/editor-common/analytics/types/inline-comment-events';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import { CommentBadgeNext } from '@atlaskit/editor-common/media-single/CommentBadgeNext';
import { ExternalImageBadge } from '@atlaskit/editor-common/media-single/ExternalImageBadge';
import { MediaBadges } from '@atlaskit/editor-common/media-single/MediaBadges';
import { MediaBorderGapFiller } from '@atlaskit/editor-common/MediaSingle/styled';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory/context-identifier-provider';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { WithProviders } from '@atlaskit/editor-common/provider-factory/with-providers';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { hexToEditorBorderPaletteColor } from '@atlaskit/editor-palette/border';
import type { MediaFeatureFlags } from '@atlaskit/media-common/types';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import { token } from '@atlaskit/tokens';

import type { AnalyticsEventPayload } from '../../../analytics/events';
import { MODE, PLATFORM } from '../../../analytics/events';
import type { MediaRenderEvent, MediaRenderEventPayload } from '../../../types/mediaOptions';
import { useAnnotationRangeState } from '../../../ui/annotations/contexts/AnnotationRangeContext';
import { useInlineCommentSubscriberContext } from '../../../ui/annotations/hooks/use-inline-comment-subscriber';
import { useInlineCommentsFilter } from '../../../ui/annotations/hooks/use-inline-comments-filter';
import type { MediaCardProps, MediaProvider } from '../../../ui/MediaCard';
import { MediaCard } from '../../../ui/MediaCard';
import { getEventHandler } from '../../../utils';
import AnnotationComponent from '../../marks/annotation';

export type MediaProps = MediaCardProps & {
	allowAltTextOnImages?: boolean;
	allowAnnotationsDraftMode?: boolean;
	children?: React.ReactNode;
	enableDownloadButton?: boolean;
	eventHandlers?: EventHandlers;
	featureFlags?: MediaFeatureFlags;
	fireAnalyticsEvent?: (event: AnalyticsEventPayload) => void;
	height?: number;
	isAnnotationMark?: () => boolean;
	isBorderMark: () => boolean;
	isDrafting: boolean;
	isInsideOfBlockNode?: boolean;
	isLinkMark: () => boolean;
	marks: Array<
		LinkDefinition | BorderMarkDefinition | AnnotationMarkDefinition | DataConsumerDefinition
	>;
	// only used for comment badge, is injected via nodes/mediaSingle
	mediaSingleElement?: HTMLElement | null;
	/** Extensions for the media viewer (e.g. sidebar with comment indicator). */
	mediaViewerExtensions?: MediaViewerExtensions;
	onMediaRenderEvent?: (event: MediaRenderEvent) => void;
	providers?: ProviderFactory;
	// attributes for media node
	width?: number;
};

type Providers = {
	contextIdentifierProvider?: Promise<ContextIdentifierProvider>;
	mediaProvider?: Promise<MediaProvider>;
};

const getDataConsumerMark = (marks: MediaProps['marks']): DataConsumerDefinition | undefined =>
	marks.find(
		(mark) =>
			mark.type === 'dataConsumer' ||
			(mark.type as unknown as { name: string })?.name === 'dataConsumer',
	) as DataConsumerDefinition | undefined;

const linkStyle = css({
	position: 'absolute',
	background: 'transparent',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	width: '100% !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	height: '100% !important',
});

const borderStyle = css({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	width: '100% !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	height: '100% !important',
});

const MediaBorder = ({
	mark,
	children,
}: React.PropsWithChildren<{
	mark?: BorderMarkDefinition;
}>): JSX.Element => {
	if (!mark) {
		return <Fragment>{children}</Fragment>;
	}

	const borderColor = mark?.attrs.color ?? '';
	const borderWidth = mark?.attrs.size ?? 0;

	const paletteColorValue = hexToEditorBorderPaletteColor(borderColor) || borderColor;

	const borderRadius = token('radius.large', '8px');

	return (
		<div
			data-mark-type="border"
			data-color={borderColor}
			data-size={borderWidth}
			css={borderStyle}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				borderRadius,
				boxShadow: `0 0 0 ${borderWidth}px ${paletteColorValue}`,
			}}
		>
			<MediaBorderGapFiller borderColor={borderColor} />
			{children}
		</div>
	);
};

const MediaLink = ({
	mark,
	children,
	onClick,
}: React.PropsWithChildren<{
	mark?: LinkDefinition;
	onClick?: React.MouseEventHandler<HTMLAnchorElement> | undefined;
}>): JSX.Element => {
	if (!mark) {
		return <Fragment>{children}</Fragment>;
	}
	const linkHref = mark?.attrs.href;

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a
			href={linkHref}
			rel="noreferrer noopener"
			onClick={onClick}
			data-block-link={linkHref}
			css={linkStyle}
		>
			{children}
		</a>
	);
};

const MediaAnnotation = ({
	mark,
	children,
}: React.PropsWithChildren<{
	mark?: AnnotationMarkDefinition;
}>): JSX.Element => {
	if (!mark) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<AnnotationComponent
			id={mark.attrs.id}
			annotationType={mark.attrs.annotationType}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			dataAttributes={{
				'data-renderer-mark': true,
				'data-block-mark': true,
			}}
			// This should be fine being empty [] since the serializer serializeFragmentChild getMarkProps call always passes
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			annotationParentIds={[]}
			allowAnnotations
			useBlockLevel
		>
			{children}
		</AnnotationComponent>
	);
};

const MediaAnnotations = ({
	marks = [],
	children,
}: React.PropsWithChildren<{
	marks?: AnnotationMarkDefinition[];
}>): JSX.Element => {
	// Early Exit
	if (marks.length === 0) {
		return <Fragment>{children}</Fragment>;
	}

	// Recursive marks
	const currentMark = marks[0];
	const otherMarks = marks.slice(1);

	return (
		<Fragment>
			<MediaAnnotation key={currentMark.attrs.id} mark={currentMark}>
				{otherMarks.length ? (
					<MediaAnnotations marks={otherMarks}>{children}</MediaAnnotations>
				) : (
					<Fragment>{children}</Fragment>
				)}
			</MediaAnnotation>
		</Fragment>
	);
};

type CommentBadgeWrapperProps = {
	isDrafting?: boolean;
	marks?: AnnotationMarkDefinition[];
	mediaSingleElement?: HTMLElement | null;
};

const CommentBadgeWrapper = ({
	marks,
	mediaSingleElement,
	isDrafting = false,
	...rest
}: CommentBadgeWrapperProps) => {
	const [status, setStatus] = useState<'default' | 'active'>('default');
	const [entered, setEntered] = useState(false);
	const updateSubscriber = useInlineCommentSubscriberContext();
	const activeParentIds = useInlineCommentsFilter({
		annotationIds: marks?.map((mark) => mark.attrs.id) ?? [''],
		filter: {
			state: AnnotationMarkStates.ACTIVE,
		},
	});

	useEffect(() => {
		const observer = new MutationObserver((mutationList) => {
			mutationList.forEach((mutation) => {
				const parentNode = mutation.target.parentNode as Element | null;
				if (mutation.attributeName === 'data-has-focus') {
					const isMediaCaption = parentNode?.closest('[data-media-caption="true"]');
					const elementHasFocus =
						parentNode?.querySelector('[data-has-focus="true"]') && !isMediaCaption;
					elementHasFocus ? setStatus('active') : setStatus('default');
				}
			});
		});

		if (mediaSingleElement) {
			observer.observe(mediaSingleElement, {
				attributes: true,
				subtree: true,
				attributeFilter: ['data-has-focus'],
			});
		}

		return () => {
			observer.disconnect();
		};
	}, [mediaSingleElement, setStatus]);

	if (!isDrafting && !activeParentIds.length) {
		return null;
	}

	const onClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (updateSubscriber) {
			updateSubscriber.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: activeParentIds,
				// Ignored via go/ees005
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				eventTarget: e.target as HTMLElement,
				// use mediaSingle here to align with annotation viewed event dispatched in editor
				eventTargetType: 'mediaSingle',
				viewMethod: VIEW_METHOD.BADGE,
			});
		}
	};

	return (
		<CommentBadgeNext
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onMouseEnter={() => setEntered(true)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onMouseLeave={() => setEntered(false)}
			status={entered ? 'entered' : status}
			onClick={onClick}
			mediaSingleElement={mediaSingleElement}
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...rest}
		/>
	);
};

// Ignored via go/ees005
// eslint-disable-next-line @repo/internal/react/no-class-components
class Media extends PureComponent<MediaProps, object> {
	private readonly mediaInstance = {};

	constructor(props: MediaProps) {
		super(props);
		this.handleMediaLinkClickFn = this.handleMediaLinkClick.bind(this);
	}
	private handleMediaLinkClickFn;

	private getDataConsumerMark = (): DataConsumerDefinition | undefined =>
		getDataConsumerMark(this.props.marks);

	private getDataConsumerSource = (props: MediaProps = this.props): string | undefined =>
		getDataConsumerMark(props.marks)?.attrs.sources?.[0];

	private emitMediaRenderEvent = (
		event: MediaRenderEventPayload,
		props: MediaProps = this.props,
	): void => {
		props.onMediaRenderEvent?.({
			...event,
			dataConsumerSource: this.getDataConsumerSource(props),
			mediaId: props.id,
			mediaInstance: this.mediaInstance,
		});
	};

	componentDidMount(): void {
		this.emitMediaRenderEvent({ type: 'mounted' });
		const dataConsumerMark = this.getDataConsumerMark();
		const infographicType = dataConsumerMark?.attrs.sources?.[0];
		if (infographicType && this.props.fireAnalyticsEvent) {
			this.props.fireAnalyticsEvent({
				action: ACTION.RENDERED,
				actionSubject: ACTION_SUBJECT.MEDIA,
				actionSubjectId: this.props.id,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					infographicType,
					pageMode: 'view',
					mediaId: this.props.id,
				},
			});
		}
	}

	componentDidUpdate(prevProps: MediaProps): void {
		if (
			prevProps.id !== this.props.id ||
			this.getDataConsumerSource(prevProps) !== this.getDataConsumerSource() ||
			prevProps.onMediaRenderEvent !== this.props.onMediaRenderEvent
		) {
			this.emitMediaRenderEvent({ type: 'unmounted' }, prevProps);
			this.emitMediaRenderEvent({ type: 'mounted' });
		}
	}

	componentWillUnmount(): void {
		this.emitMediaRenderEvent({ type: 'unmounted' });
	}

	private onPreviewRender = (renderedMediaId: string): void => {
		this.props.onPreviewRender?.(renderedMediaId);
		this.emitMediaRenderEvent({ renderedMediaId, type: 'preview-rendered' });
	};

	private onError: NonNullable<MediaCardProps['onError']> = (reason): void => {
		this.props.onError?.(reason);
		this.emitMediaRenderEvent({ reason, type: 'error' });
	};

	private renderCard = (providers: Providers = {}) => {
		const { contextIdentifierProvider } = providers;
		const {
			allowAltTextOnImages,
			alt,
			featureFlags,
			shouldOpenMediaViewer: allowMediaViewer,
			enableDownloadButton,
			ssr,
			width,
			height,
			mediaSingleElement,
			isDrafting = false,
			mediaViewerExtensions,
		} = this.props;

		const annotationMarks = (
			this.props.isAnnotationMark ? this.props.marks.filter(this.props.isAnnotationMark) : undefined
		) as AnnotationMarkDefinition[] | undefined;

		const borderMark = this.props.marks.find(this.props.isBorderMark) as
			| BorderMarkDefinition
			| undefined;

		const dataConsumerMark = this.getDataConsumerMark();

		const linkMark = this.props.marks.find(this.props.isLinkMark) as LinkDefinition | undefined;

		const linkHref = linkMark?.attrs.href;
		const eventHandlers = linkHref ? undefined : this.props.eventHandlers;
		const shouldOpenMediaViewer = !linkHref && allowMediaViewer;
		const isInPageInclude = mediaSingleElement?.closest('[data-node-type="include"]');
		const isIncludeExcerpt = !!mediaSingleElement?.closest('.ak-excerpt-include');

		const showCommentBadge = !!annotationMarks && !isInPageInclude && !isIncludeExcerpt;

		return (
			<MediaLink mark={linkMark} onClick={this.handleMediaLinkClickFn}>
				<MediaAnnotations marks={annotationMarks}>
					<MediaBorder mark={borderMark}>
						<AnalyticsContext
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							data={{
								[MEDIA_CONTEXT]: {
									border: !!borderMark,
									// Only defined for remix-generated media (i.e. media nodes with a dataConsumer mark).
									// Format: "remix:{type}:{subtype}" e.g. "remix:infographic:corporate-doodle"
									remixSource: dataConsumerMark?.attrs.sources?.[0],
								},
							}}
						>
							<MediaBadges
								mediaElement={mediaSingleElement}
								mediaWidth={width}
								mediaHeight={height}
								useMinimumZIndex
							>
								{({ visible }: { visible: boolean }) => (
									<React.Fragment>
										{visible && (
											<ExternalImageBadge
												type={this.props.type}
												url={this.props.type === 'external' ? this.props.url : undefined}
											/>
										)}
										{showCommentBadge && (
											<CommentBadgeWrapper
												marks={annotationMarks}
												mediaSingleElement={mediaSingleElement}
												isDrafting={isDrafting}
											/>
										)}
									</React.Fragment>
								)}
							</MediaBadges>
							<MediaCard
								contextIdentifierProvider={contextIdentifierProvider}
								// Ignored via go/ees005
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...this.props}
								shouldOpenMediaViewer={shouldOpenMediaViewer}
								eventHandlers={eventHandlers}
								alt={allowAltTextOnImages ? alt : undefined}
								featureFlags={featureFlags}
								shouldEnableDownloadButton={enableDownloadButton}
								ssr={ssr}
								onError={
									this.props.onMediaRenderEvent || this.props.onError ? this.onError : undefined
								}
								onPreviewRender={
									this.props.onMediaRenderEvent || this.props.onPreviewRender
										? this.onPreviewRender
										: undefined
								}
								mediaViewerExtensions={mediaViewerExtensions}
							/>
						</AnalyticsContext>
					</MediaBorder>
				</MediaAnnotations>
			</MediaLink>
		);
	};

	private handleMediaLinkClick = (event: SyntheticEvent<HTMLAnchorElement, Event>) => {
		const { fireAnalyticsEvent, isLinkMark, marks } = this.props;
		if (fireAnalyticsEvent) {
			fireAnalyticsEvent({
				action: ACTION.VISITED,
				actionSubject: ACTION_SUBJECT.MEDIA,
				actionSubjectId: ACTION_SUBJECT_ID.LINK,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					platform: PLATFORM.WEB,
					mode: MODE.RENDERER,
				},
			});
		}
		const linkMark = this.props.marks.find(this.props.isLinkMark) as LinkDefinition;
		const linkHref = linkMark?.attrs.href;

		const handler = getEventHandler(this.props.eventHandlers, 'link');
		if (handler) {
			const linkMark = marks.find(isLinkMark);
			handler(event, linkMark && linkHref);
		}
	};

	render() {
		const { providers } = this.props;
		if (!providers) {
			return this.renderCard();
		}
		return (
			<WithProviders
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				providers={['mediaProvider', 'contextIdentifierProvider']}
				providerFactory={providers}
				renderNode={this.renderCard}
			/>
		);
	}
}

const MediaWithDraftAnnotation = (props: PropsWithChildren<MediaProps>): jsx.JSX.Element => {
	const { hoverDraftDocumentPosition: draftPosition } = useAnnotationRangeState();

	const { dataAttributes } = props;
	const pos = dataAttributes && dataAttributes['data-renderer-start-pos'];

	const [position, setPosition] = useState<number | undefined>();
	const [shouldApplyDraftAnnotation, setShouldApplyDraftAnnotation] = useState<boolean>(false);
	useEffect(() => {
		if (pos === undefined) {
			return;
		}
		const posToCheck = (draftPosition?.from ?? 0) + 1;

		if (draftPosition !== null && posToCheck === pos) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			setShouldApplyDraftAnnotation(true);
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			setPosition(posToCheck);
		} else if (draftPosition === null && shouldApplyDraftAnnotation) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			setShouldApplyDraftAnnotation(false);
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			setPosition(undefined);
		}
	}, [draftPosition, pos, shouldApplyDraftAnnotation]);

	const applyDraftAnnotation =
		props.allowAnnotationsDraftMode && shouldApplyDraftAnnotation && position !== undefined;

	const dataAttributesWithDraftAnnotation = useMemo(
		() =>
			applyDraftAnnotation
				? {
						...dataAttributes,
						'data-annotation-draft-mark': true,
						'data-renderer-mark': true,
					}
				: dataAttributes,
		[applyDraftAnnotation, dataAttributes],
	);

	return (
		<Media
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			dataAttributes={dataAttributesWithDraftAnnotation}
			isDrafting={shouldApplyDraftAnnotation}
		/>
	);
};

export default MediaWithDraftAnnotation;
